<template>
  <div
    class="px-6 border-b border-mid-neutral-50 sticky left-0 top-0 z-[52] bg-white"
  >
    <div class="flex justify-between items-center w-full">
      <div>
        <template v-for="r in routes" :key="'tab-' + r.name">
          <button
            class="p-4 text-sm font-medium relative border-b-2"
            :class="{
              'text-primary-500   border-primary-500': r.name === route.name,
              'text-gray-500 border-transparent': r.name !== route.name,
              '!text-gray-300 border-transparent': r.disabled,
            }"
            :disabled="r.disabled"
            @click="router.push({ name: r.name })"
          >
            {{ r.title }}
          </button>
        </template>
      </div>

      <div class="flex items-center gap-1">
        <div class="flex items-center border border-gray-200 rounded-lg">
          <FloatingMenu placement="bottom" :offset="[0, 8]">
            <template #activator="{ show }">
              <button
                class="p-2 text-gray-600 hover:text-purple-600 hover:bg-gray-50 transition-colors border-r border-gray-200"
                title="AI Assistants Settings"
                @click="show"
              >
                <SparklesIcon class="h-5 w-5" />
              </button>
            </template>
            <template #content>
              <div
                class="flex flex-col bg-white rounded-lg shadow-lg p-4 gap-3 min-w-64"
              >
                <div class="text-sm font-semibold text-gray-700 mb-1">
                  AI Assistants
                </div>
                <Switch
                  v-model="autoScreeningEnabled"
                  :disabled="loading"
                  @update:model-value="handleToggleScreening"
                >
                  <span class="text-sm text-gray-700">Auto Screening</span>
                </Switch>
                <Switch
                  v-model="autoExtractionEnabled"
                  :disabled="loading"
                  @update:model-value="handleToggleExtraction"
                >
                  <span class="text-sm text-gray-700">Auto Extraction</span>
                </Switch>
              </div>
            </template>
          </FloatingMenu>

          <JobsStatusModal :review-id="review.entity.value.id" />
        </div>
      </div>
    </div>
  </div>

  <router-view v-slot="{ Component }">
    <keep-alive>
      <component :is="Component" />
    </keep-alive>
  </router-view>
</template>
<script setup lang="ts">
import { computed, ref, onMounted } from 'vue'
import { injectStrict } from '@app/utils/injectStrict'
import { ReviewKey } from '../use-review'

import { SnackbarState } from '@app/types'

import JobsStatusModal from '@app/components/Review/JobsStatusModal.vue'
import FloatingMenu from '@app/components/Global/FloatingMenu.vue'
import Switch from '@app/components/Global/Switch.vue'
import SparklesIcon from '@app/components/Icons/SparklesIcon.vue'
import { useRoute, useRouter } from 'vue-router'
import useSnackbar from '@app/composables/use-snackbar'

const review = injectStrict(ReviewKey)
const snackbar = useSnackbar()

const loading = ref(false)
const isAutoScreeningPaused = ref(true)
const isAutoExtractionPaused = ref(true)

const autoScreeningEnabled = computed({
  get: () => !isAutoScreeningPaused.value,
  set: () => {},
})

const autoExtractionEnabled = computed({
  get: () => !isAutoExtractionPaused.value,
  set: () => {},
})

const route = useRoute()
const router = useRouter()

function fetchPauseState() {
  const plan = review.entity.value.plan as any
  isAutoScreeningPaused.value = plan?.isAutoScreeningPaused ?? true
  isAutoExtractionPaused.value = plan?.isAutoExtractionPaused ?? true
}

async function handleToggleScreening() {
  loading.value = true
  try {
    if (isAutoScreeningPaused.value) {
      await review.resumeAutoScreening()
      await review.autoScreen()
      snackbar.success('Success')
    } else {
      await review.pauseAutoScreening()
      snackbar.success('Success')
    }
    fetchPauseState()
  } catch (error: any) {
    snackbar.show(
      SnackbarState.ERROR,
      error.message || 'Failed to toggle auto screening',
    )
  } finally {
    loading.value = false
  }
}

async function handleToggleExtraction() {
  loading.value = true
  try {
    if (isAutoExtractionPaused.value) {
      await review.resumeAutoExtraction()
      await review.autoExtraction()
      snackbar.success('Success')
    } else {
      await review.pauseAutoExtraction()
      snackbar.success('Success')
    }
    fetchPauseState()
  } catch (error: any) {
    snackbar.show(
      SnackbarState.ERROR,
      error.message || 'Failed to toggle auto extraction',
    )
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  fetchPauseState()
})

const routes = computed(() => {
  const rts = [
    {
      name: 'Review - Execute - Screening',
      title: 'Screening',
    },

    {
      name: 'Review - Execute - Appraisal',
      title: 'Appraisal',
      disabled:
        !review.entity.value?.plan?.appraisalPlan.isImdrfMdce2019Applicable &&
        !review.entity.value?.plan?.appraisalPlan
          .isOxfordLevelOfEvidenceApplicable &&
        !review.entity.value?.plan?.appraisalPlan.isPeerReviewStatusApplicable,
    },

    {
      name: 'Review - Execute - Data Extraction',
      title: 'Extraction',
    },

    {
      name: 'Review - Execute - Synthesis',
      title: 'Synthesis',
    },
  ]
  const siblingReviews = review.entity.value.reviewSeries.reviews.toSorted(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  )
  const isFirstReview = siblingReviews[0].id === review.entity.value.id
  if (!isFirstReview) {
    rts.push({
      name: 'Review - Execute - Synthesis Delta',
      title: 'Changes',
    })
  }
  return rts
})
</script>
